import { useEffect, useRef } from "react";
import SockJS from "sockjs-client";
import { guardURL } from "../../../../Apis/Constants";
import { TriggerNotificationsByUser } from "../../../../reduxToolkit/Reports/ReportApi";
import { setErrorCode, setNotificationList } from "../../../../reduxToolkit/Reports/ReportSlice";
import { ACTION_MODEL } from "../../../../Helpers/ActionModel";
import { MODELS_NAME } from "../../../../Apis/Models";
import playSound from "../../../../utils/PlaySound";
import { toast } from "react-toastify";
import { errorMsg } from "../../../../Apis/functions";
import { Client } from "@stomp/stompjs";

/**
 * @author Karen Yunuen Pozos Arias
 * @jobTitle Full Stack Developer - IBL
 * @email kpozos@ibl.mx
 *
 * Component that handles WebSocket notifications for a user.
 * It establishes a STOMP connection, subscribes to topics,
 * and dispatches Redux actions upon receiving messages.
 * Supports reconnection on network recovery and tab visibility changes.
 *
 * @param {Object} props
 * @param {string} props.tokenDecrpt - JWT token for authentication.
 * @param {Object} props.user - Current user object.
 * @param {Function} props.dispatch - Redux dispatch function.
 * @param {Function} props.t - Translation function.
 */
export const NotificationsWebSocketApp = ({ tokenDecrpt, user, dispatch, t }) => {
    /** Ref to hold the STOMP client instance */
    const clientRef = useRef(null);
    /** Ref to store pending reconnection timeout */
    const reconnectTimeoutRef = useRef(null);
    /** Ref to prevent multiple simultaneous reconnection attempts */
    const isReconnectingRef = useRef(false);
    /** Ref to mark if the connection has succeeded */
    const connectionSuccessRef = useRef(false);

    /** 🔹 Completely closes the client and underlying socket */
    const destroyWebSocket = async () => {
        // Clear any pending reconnection timeout
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }

        const client = clientRef.current;
        if (!client) return;

        try {
            await client.deactivate(); // Deactivate STOMP client
        } catch (e) {}

        // Force-close the underlying SockJS socket
        try {
            if (client.webSocket) client.webSocket.close();
            else if (client._webSocket) client._webSocket.close();
            else if (client.webSocketFactoryInstance?._transport?.websocket)
                client.webSocketFactoryInstance._transport.websocket.close();
        } catch (e) {}

        clientRef.current = null;
        connectionSuccessRef.current = false;
        isReconnectingRef.current = false;
    };

    /** 🔹 Schedules a reconnection attempt */
    const scheduleReconnect = (delay = 5000) => {
        if (reconnectTimeoutRef.current || isReconnectingRef.current) return;
        isReconnectingRef.current = true;

        reconnectTimeoutRef.current = setTimeout(async () => {
            reconnectTimeoutRef.current = null;
            await destroyWebSocket(); // Close existing connection
            initializeWebSocket();     // Try to reconnect
        }, delay);
    };

    /** 🔹 Initializes a fresh WebSocket connection */
    const initializeWebSocket = async () => {
        if (!tokenDecrpt || !user?.id) return;

        // Close any existing connection
        await destroyWebSocket();

        const sock = new SockJS(`${guardURL}?token=${tokenDecrpt}`);
        const client = new Client({
            webSocketFactory: () => sock,
            connectHeaders: { Authorization: `Bearer ${tokenDecrpt}` },
            reconnectDelay: 0, // Disable automatic STOMP reconnection
            heartbeatIncoming: 10000,
            heartbeatOutgoing: 10000,
            debug: () => {},

            /** Called when STOMP connection succeeds */
            onConnect: () => {
                connectionSuccessRef.current = true;
                isReconnectingRef.current = false;

                // Clear any pending reconnection attempt
                if (reconnectTimeoutRef.current) {
                    clearTimeout(reconnectTimeoutRef.current);
                    reconnectTimeoutRef.current = null;
                }

                // Trigger initial notifications fetch
                dispatch(TriggerNotificationsByUser({ userId: user.id, token: tokenDecrpt }));

                // Subscribe to user-specific notifications topic
                client.subscribe(`/topic/notifications/${user.id}`, (message) => {
                    try {
                        const receivedData = JSON.parse(message.body);
                        dispatch(setErrorCode(receivedData?.data));

                        if (
                            receivedData?.data === ACTION_MODEL.OK &&
                            receivedData?.action === ACTION_MODEL.CREATE &&
                            Object.entries(receivedData?.item || {}).length > 0
                        ) {
                            dispatch(setNotificationList(receivedData?.item || []));
                            if (receivedData?.model === MODELS_NAME.report) {
                                playSound.play();
                                toast.info(t("file_ready_for_download"), { autoClose: false, closeButton: true });
                            }
                        } else {
                            errorMsg(receivedData?.data);
                            dispatch(setNotificationList(receivedData?.item || []));
                        }
                    } catch (error) {
                        errorMsg(error?.message);
                    }
                });
            },

            /** Called when WebSocket is closed */
            onWebSocketClose: () => {
                connectionSuccessRef.current = false;
                scheduleReconnect(); // Attempt to reconnect
            },

            /** Called when WebSocket encounters an error */
            onWebSocketError: () => {
                connectionSuccessRef.current = false;
                scheduleReconnect();
            },

            /** Called when STOMP reports an error */
            onStompError: () => {
                connectionSuccessRef.current = false;
                scheduleReconnect();
            }
        });

        clientRef.current = client;
        client.activate();

        // Safety timeout: if connection is not established in 10s, retry
        setTimeout(() => {
            if (!client.connected && !connectionSuccessRef.current) {
                destroyWebSocket().then(() => scheduleReconnect(4000));
            }
        }, 10000);
    };

    /** 🔹 React effect for mounting and cleanup */
    useEffect(() => {
        // Initialize WebSocket on mount or when user/token changes
        initializeWebSocket();

        /** Handler to re-initialize on network recovery */
        const handleOnline = () => initializeWebSocket();

        /** Handler to re-initialize when tab becomes visible */
        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible" && (!clientRef.current || !clientRef.current.connected)) {
                initializeWebSocket();
            }
        };

        window.addEventListener("online", handleOnline);
        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            window.removeEventListener("online", handleOnline);
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            clearTimeout(reconnectTimeoutRef.current);
            destroyWebSocket();
        };
    }, [user?.id, tokenDecrpt]);

    return {};
};
